
import { useState, useEffect, lazy, Suspense } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { ArrowRight, Video, Star, Globe } from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';
import { therapistsData } from '@/data/therapists';
import { TherapistProps } from '@/components/therapists/TherapistCard';
import OptimizedImage from '@/components/shared/OptimizedImage';
import { T } from '@/components/T';

// Optimized smaller therapist card component specific for this section
const OnlineTherapistCard = ({ therapist }: { therapist: TherapistProps }) => {
  return (
    <Card className="overflow-hidden animate-fade-in">
      <CardContent className="p-0">
        <div className="flex items-center p-4 border-b border-brand-gray-100">
          <div className="w-12 h-12 rounded-full bg-brand-blue-100 overflow-hidden flex items-center justify-center mr-3">
            {therapist.image ? (
              <OptimizedImage
                src={therapist.image}
                alt={`${therapist.name}, psychotherapist`}
                className="w-full h-full object-cover"
                width={48}
                height={48}
              />
            ) : (
              <span className="text-xl font-bold text-brand-blue-500">
                {therapist.name.charAt(0)}
              </span>
            )}
          </div>
          <div>
            <h3 className="font-medium text-brand-gray-900">{therapist.name}</h3>
            <div className="flex items-center text-sm">
              <Star className="h-3 w-3 text-yellow-400 mr-1" />
              <span className="text-brand-gray-700">{therapist.rating}</span>
              <span className="text-brand-gray-500 ml-1">({therapist.reviewCount})</span>
            </div>
          </div>
          <Badge className="ml-auto bg-green-500 text-white">
            <Video className="mr-1 h-3 w-3" />
            Online
          </Badge>
        </div>
        
        <div className="p-4">
          <div className="flex items-center text-sm text-brand-gray-600 mb-2">
            <Globe className="h-4 w-4 mr-1 text-brand-gray-400" />
            <span>{therapist.languages?.join(', ')}</span>
          </div>
          
          <div className="flex flex-wrap gap-1 mb-4">
            {therapist.specialties.slice(0, 2).map((specialty, index) => (
              <Badge 
                key={index}
                variant="secondary" 
                className="bg-brand-gray-100 text-brand-gray-700 text-xs"
              >
                {specialty}
              </Badge>
            ))}
          </div>
          
          <div className="flex justify-between items-center">
            <div>
              <span className="text-lg font-semibold text-brand-gray-900">
                {therapist.currency}{therapist.price}
              </span>
              <span className="text-sm text-brand-gray-500 ml-1">/ hour</span>
            </div>
            
            <Button 
              className="bg-green-600 hover:bg-green-700 text-white"
              asChild
            >
              <Link to={`/therapist/${therapist.id}`}>
                <T>Connect Now</T>
              </Link>
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

// Skeleton for loading state
const OnlineTherapistCardSkeleton = () => (
  <Card className="overflow-hidden">
    <CardContent className="p-0">
      <div className="flex items-center p-4 border-b border-brand-gray-100">
        <Skeleton className="w-12 h-12 rounded-full mr-3" />
        <div className="flex-1">
          <Skeleton className="h-5 w-32 mb-1" />
          <Skeleton className="h-4 w-20" />
        </div>
        <Skeleton className="h-6 w-16 ml-auto" />
      </div>
      
      <div className="p-4">
        <Skeleton className="h-4 w-36 mb-2" />
        <div className="flex gap-1 mb-4">
          <Skeleton className="h-6 w-20" />
          <Skeleton className="h-6 w-24" />
        </div>
        <div className="flex justify-between items-center">
          <Skeleton className="h-6 w-20" />
          <Skeleton className="h-10 w-28" />
        </div>
      </div>
    </CardContent>
  </Card>
);

const AvailableOnlineSection = () => {
  const [therapists, setTherapists] = useState<TherapistProps[]>([]);
  const [selectedLanguages, setSelectedLanguages] = useState<string[]>(["English", "Spanish", "Hindi"]);
  const [isLoading, setIsLoading] = useState(true);
  
  useEffect(() => {
    // Simulate network delay for data loading
    const timer = setTimeout(() => {
      const filteredTherapists = therapistsData
        .filter((therapist) => 
          therapist.isOnline && 
          therapist.available && 
          therapist.languages?.some(lang => selectedLanguages.includes(lang))
        )
        .sort((a, b) => {
          const scoreA = (a.sessionCount || 0) * 0.6 + (a.rating * a.reviewCount) * 0.4;
          const scoreB = (b.sessionCount || 0) * 0.6 + (b.rating * b.reviewCount) * 0.4;
          return scoreB - scoreA;
        })
        .slice(0, 3); // Only show top 3 for performance
      
      setTherapists(filteredTherapists);
      setIsLoading(false);
    }, 150);
    
    return () => clearTimeout(timer);
  }, [selectedLanguages]);
  
  const handleLanguageToggle = (language: string) => {
    setIsLoading(true);
    setSelectedLanguages(prev => 
      prev.includes(language) 
        ? prev.filter(l => l !== language)
        : [...prev, language]
    );
  };

  return (
    <section className="py-8 bg-gradient-to-b from-brand-blue-50 to-white">
      <div className="container mx-auto px-4">
        <div className="flex flex-col md:flex-row justify-between items-center mb-6">
          <div className="mb-6 md:mb-0 text-center md:text-left">
            <h2 className="text-2xl md:text-3xl font-bold text-brand-gray-900 mb-2">
              <span className="text-green-600"><T>Available Online Now</T></span>
            </h2>
            <p className="text-brand-gray-600">
              <T>Connect instantly with therapists ready to help in your preferred language</T>
            </p>
          </div>
          
          <div className="flex gap-2 flex-wrap">
            {["English", "Spanish", "Hindi", "Russian", "Japanese"].map(language => (
              <Button
                key={language}
                variant={selectedLanguages.includes(language) ? "default" : "outline"}
                size="sm"
                onClick={() => handleLanguageToggle(language)}
                className={selectedLanguages.includes(language) 
                  ? "bg-brand-blue-500" 
                  : "border-brand-gray-300"
                }
              >
                {language}
              </Button>
            ))}
          </div>
        </div>
        
        {isLoading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {Array.from({ length: 3 }).map((_, index) => (
              <OnlineTherapistCardSkeleton key={`skeleton-${index}`} />
            ))}
          </div>
        ) : therapists.length === 0 ? (
          <Card className="text-center p-8">
            <CardContent className="pt-6">
              <p className="text-brand-gray-600"><T>No therapists available right now in your selected languages.</T></p>
              <p className="text-brand-gray-600 mt-2"><T>Try selecting different languages or check back later.</T></p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {therapists.map((therapist) => (
              <OnlineTherapistCard key={therapist.id} therapist={therapist} />
            ))}
          </div>
        )}
        
        <div className="text-center mt-6">
          <Button
            variant="outline"
            className="border-brand-blue-200 text-brand-blue-600 hover:bg-brand-blue-50"
            asChild
          >
            <Link to="/find">
              <T>View All Available Therapists</T>
              <ArrowRight className="ml-2 h-4 w-4" />
            </Link>
          </Button>
        </div>
      </div>
    </section>
  );
};

export default AvailableOnlineSection;
